import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useGTM } from '@/hooks/useGTM';
import LegalLinks from '@/components/legal/LegalLinks';
import TrackingScripts from '@/components/tracking/TrackingScripts';
import { sendViewBeacon } from '@/lib/viewBeacon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Social Icons SVGs
const SocialIcons = {
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  ),
  email: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  phone: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
    </svg>
  ),
  website: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  ),
};

export default function PublicBioPage({ preview = false }) {
  const { slug } = useParams();
  const [bio, setBio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { trackBioPageView, trackBioLinkClick, gtmEnabled } = useGTM({ preview });
  
  useEffect(() => {
    fetchBio();
  }, [slug]);
  
  // One view per page load, never in preview. The bio JSON fetch records
  // nothing (link-preview crawlers hit it too); the beacon is the view.
  const viewSentFor = useRef(null);
  useEffect(() => {
    if (!bio || preview) return;
    if (viewSentFor.current !== slug) {
      viewSentFor.current = slug;
      sendViewBeacon({ kind: 'bio', slug });
    }
  }, [bio, slug, preview]);

  // GTM view (a no-op until GTM's config has loaded, then fires once).
  useEffect(() => {
    if (bio && bio.profile?.display_name && !preview) {
      trackBioPageView(bio.id, bio.profile.display_name, slug);
    }
  }, [bio, slug, trackBioPageView, preview]);
  
  const fetchBio = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/bio/public/${slug}`);
      const data = await response.json();
      
      if (data.success) {
        setBio(data.data);
      } else {
        setError('Page not found');
      }
    } catch (err) {
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLinkClick = async (link) => {
    // Track click in GA4 (client-side)
    trackBioLinkClick(bio.id, link.title, link.url);
    // Track click via redirect endpoint (server-side tracking)
    const trackUrl = `${BACKEND_URL}/api/v1/public/bio/click/${bio.id}/${link.id}`;
    window.open(trackUrl, '_blank');
  };
  
  const handleSocialClick = async (social) => {
    // Track click in GA4 (client-side)
    trackBioLinkClick(bio.id, social.platform, social.url);
    // Track click via redirect endpoint (server-side tracking)
    const trackUrl = `${BACKEND_URL}/api/v1/public/bio/click/${bio.id}/${social.id}`;
    window.open(trackUrl, '_blank');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }
  
  if (error || !bio) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
          <p className="text-gray-400">This bio link page doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }
  
  const theme = bio.theme || {};
  const profile = bio.profile || {};
  const links = bio.links || [];
  const socialLinks = bio.social_links || [];
  
  // Build background style
  const bgStyle = theme.background_type === 'gradient' && theme.background_gradient
    ? { background: theme.background_gradient }
    : { backgroundColor: theme.background_color || '#0f172a' };
  
  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };
  
  return (
    <div
      className="min-h-screen py-12 px-4"
      style={bgStyle}
      data-testid="public-bio-page"
    >
      <div className="max-w-md mx-auto">
        {/* Profile */}
        <div className="text-center mb-8">
          {/* Avatar - supports circle or logo (full rectangular) display */}
          {profile.avatar_shape === 'logo' ? (
            // Full Logo Display (rectangular, no cropping)
            profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.display_name || 'Logo'}
                className="max-w-[280px] max-h-24 mx-auto object-contain"
                data-testid="bio-logo-image"
              />
            ) : (
              <div
                className="max-w-[200px] h-16 mx-auto rounded flex items-center justify-center text-sm font-medium"
                style={{
                  backgroundColor: theme.button_color || '#3b82f6',
                  color: theme.button_text_color || '#ffffff'
                }}
              >
                Your Logo
              </div>
            )
          ) : (
            // Circle Avatar Display (default)
            profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={profile.display_name}
                className="w-24 h-24 mx-auto rounded-full object-cover ring-4 ring-white/20"
                data-testid="bio-avatar-image"
              />
            ) : (
              <div
                className="w-24 h-24 mx-auto rounded-full flex items-center justify-center text-2xl font-bold ring-4 ring-white/20"
                style={{
                  backgroundColor: theme.button_color || '#3b82f6',
                  color: theme.button_text_color || '#ffffff'
                }}
              >
                {getInitials(profile.display_name)}
              </div>
            )
          )}
          
          {/* Name */}
          <h1
            className="text-xl font-bold mt-4"
            style={{ color: theme.text_color || '#ffffff' }}
          >
            {profile.display_name}
            {profile.show_verified && (
              <span className="ml-1 text-blue-400">✓</span>
            )}
          </h1>
          
          {/* Bio */}
          {profile.bio && (
            <p
              className="mt-2 text-sm opacity-80 max-w-xs mx-auto"
              style={{ color: theme.text_color || '#ffffff' }}
            >
              {profile.bio}
            </p>
          )}
        </div>
        
        {/* Links */}
        <div className="space-y-3">
          {links
            .filter(l => l.visible !== false)
            .sort((a, b) => a.order - b.order)
            .map((link) => (
              link.type === 'header' ? (
                <p
                  key={link.id}
                  className="text-xs font-medium uppercase tracking-wider text-center pt-4 opacity-60"
                  style={{ color: theme.text_color || '#ffffff' }}
                >
                  {link.title}
                </p>
              ) : (
                <button
                  key={link.id}
                  onClick={() => handleLinkClick(link)}
                  className={`w-full py-3.5 px-4 font-medium transition-all hover:scale-[1.02] active:scale-[0.98] ${
                    theme.button_style === 'outline' ? 'border-2 bg-transparent' : ''
                  } ${theme.button_radius || 'rounded-lg'}`}
                  style={{
                    backgroundColor: theme.button_style === 'outline' ? 'transparent' : (theme.button_color || '#3b82f6'),
                    borderColor: theme.button_color || '#3b82f6',
                    color: theme.button_style === 'outline' 
                      ? (theme.button_color || '#3b82f6') 
                      : (theme.button_text_color || '#ffffff'),
                    boxShadow: theme.show_shadow !== false ? '0 4px 14px rgba(0,0,0,0.15)' : 'none'
                  }}
                >
                  <span className="flex items-center justify-center gap-2">
                    {link.icon && <span>{link.icon}</span>}
                    {link.title}
                  </span>
                </button>
              )
            ))}
        </div>
        
        {/* Social Icons */}
        {socialLinks.filter(l => l.visible !== false).length > 0 && (
          <div className="flex justify-center gap-4 mt-8">
            {socialLinks
              .filter(l => l.visible !== false)
              .map((social) => (
                <button
                  key={social.id}
                  onClick={() => handleSocialClick(social)}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  style={{
                    backgroundColor: theme.button_color || '#3b82f6',
                    color: theme.button_text_color || '#ffffff'
                  }}
                  title={social.title}
                >
                  {SocialIcons[social.social_platform] || SocialIcons.website}
                </button>
              ))}
          </div>
        )}
        
        {/* Powered By */}
        <div className="mt-12 text-center">
          <p
            className="text-xs opacity-40"
            style={{ color: theme.text_color || '#ffffff' }}
          >
            Powered by Pam Golding Properties
          </p>
          <LegalLinks color={theme.text_color || '#ffffff'} className="mt-3" />
        </div>
      </div>

      {/* Cookie consent for the site-wide GTM container. Never in preview. */}
      {!preview && <TrackingScripts tags={[]} gtmEnabled={gtmEnabled} />}
    </div>
  );
}
